import cookies from 'js-cookie';
import { useEffect } from 'react';
import { Theme } from '@meitu/saas-ui';
import { bridge } from '@meitu/pc-bridge';
import { EN_DEFAULT_FONT_FAMILY_NAME, setDefaultFontFamily } from '@meitu/saas-editor-core/es/version-types';
import { useNetWork } from '@meitu/saas-editor-ui/es/hooks/use-net-work';
import { MT_COOKIE_ACCEPT } from '@meitu/xdesign-widgets/dist/collectCookie';
import eventBus from '@meitu/xdesign-widgets/dist/utils/eventBus';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { useStoreActions, useStoreState } from '@/store';
import { isPcPlatform } from '@/utils/auth';
import { getQuery, getQueryParam } from '@/utils/query';
import { useDropImageToStage } from './use-drop-image-to-stage';

const { AppTheme } = Theme;

/**
 * 主题色初始化
 */
export const useInitTheme = () => {
    const setXDesignTheme = useStoreActions((actions) => actions.editorModel.setXDesignTheme);
    const setDarkTheme = useStoreActions((actions) => actions.editorModel.setDarkTheme);
    const setWhiteTheme = useStoreActions((actions) => actions.editorModel.setWhiteTheme);
    const theme = useStoreState((state) => state.editorModel.theme);

    useEffect(() => {
        const initTheme = async () => {
            const query = getQuery();
            if (isIntl) {
                setXDesignTheme();
            } else if ((query?.theme || theme) === AppTheme.dark) {
                setDarkTheme();
            } else if (isPcPlatform()) {
                const theme = await bridge.getTheme();
                if (theme === AppTheme.dark) {
                    setDarkTheme();
                } else {
                    setWhiteTheme();
                }
            } else {
                setWhiteTheme();
            }
        };

        initTheme();
    }, []);

    useEffect(() => {
        editor.setTheme(theme);

        if (isPcPlatform()) {
            bridge.setTheme(theme);
        }

        // 国际版设置主题 cookie
        if (!isIntl) return;

        const setThemeCookie = (accept: boolean) => {
            if (accept) {
                cookies.set('theme', theme, { expires: 365 });
            } else {
                cookies.remove('theme');
            }
        };

        eventBus.on(MT_COOKIE_ACCEPT, setThemeCookie);
        return () => {
            eventBus.off(MT_COOKIE_ACCEPT, setThemeCookie);
        };
    }, [theme]);
};

/**
 * fix: 兼容 Chrome Canvas 变白问题
 */
export const useCanvasWorkAround = () => {
    useEffect(() => {
        const canvasWorkAround = () => {
            if (document.visibilityState === 'visible') {
                const canvas = document.querySelectorAll('canvas');
                canvas.forEach((item) => {
                    const context = item.getContext('2d');
                    if (!context) return;
                    context.fillStyle = 'transparent';
                    context.fillRect(0, 0, 1, 1);
                });
            }
        };

        document.addEventListener('visibilitychange', canvasWorkAround);
        return () => {
            document.removeEventListener('visibilitychange', canvasWorkAround);
        };
    }, []);
};

/**
 * 拖拽本地图片上传
 */
export const useDragLayerToStage = () => {
    const dropImageToStage = useDropImageToStage();
    useEffect(() => {
        const handle = editor.dragLayer.onUploadFile((files) => {
            dropImageToStage(files, true);
        });
        return () => {
            editor.dragLayer.offUploadFile(handle);
        };
    }, [dropImageToStage]);
};

/**
 * 粘贴文件到舞台
 */
export const usePasteFileToStage = () => {
    const dropImageToStage = useDropImageToStage();

    useEffect(() => {
        const offPasteFile = editor.clipboard.onPasteFile((files) => {
            if (editor.stage.count() > 0) {
                dropImageToStage(files);
            }
        });

        return offPasteFile;
    }, [dropImageToStage]);
};

/** 通用逻辑 */
export const useCommonLogic = () => {
    const isIntl = getQueryParam('isIntl');
    if (isIntl === '1') {
        setDefaultFontFamily(EN_DEFAULT_FONT_FAMILY_NAME);
    }

    useInitTheme();
    useCanvasWorkAround();
    usePasteFileToStage();
    useDragLayerToStage();
    useNetWork();
};
